import { useRef, useState } from 'react'
import { useOrganization } from '@clerk/clerk-react'
import type { Ctx } from '@milkdown/ctx'

import { useSnippetsEnabled } from '@/hooks/useSnippetsEnabled'
import { useSnippetEditorStore } from '@/stores/snippetEditorStore'
import { useSnippetReferenceSource } from '@/features/snippets/hooks/useSnippetReferenceSource'
import type {
  MilkdownMode,
  MilkdownSnippetContext,
} from '@/components/MilkdownMarkdownEditor'
import type { SnippetReferenceSource } from './snippetReferenceSource'

/**
 * Snippet wiring for the Crepe editor: whether the snippet UI is on for this
 * org, where resolved bodies come from, and which editor ctx the open picker
 * should insert into.
 *
 * The enabled flag is latched, and scoped to the org. `useSnippetsEnabled`
 * reads a query that returns null on error, so an unlatched value flips back to
 * false on one transient 500 — rebuilding Crepe mid-typing, since it feeds the
 * editor's dep array. But a latch that never resets would carry one org's
 * rollout into the next: `Activity` preserves this ref across hide/show, so
 * switching from an enabled org to a disabled one would keep the snippet UI.
 * Key it by org id.
 *
 * `on` and `interactive` are separate because the node SCHEMA must register in
 * preview mode too, so existing tokens render as read-only blocks there. Only
 * the toolbar button, `/snippet` and the picker are edit-only.
 */
export function useSnippetWiring(
  snippets: MilkdownSnippetContext | undefined,
  inferredMode: MilkdownMode
): {
  snippetsOn: boolean
  snippetsInteractive: boolean
  snippetDutyId: string | undefined
  snippetSource: SnippetReferenceSource
  openSnippetEditor: (snippetId: string, dutyId?: string) => void
  snippetTarget: Ctx | null
  setSnippetTarget: (target: Ctx | null) => void
} {
  const { organization } = useOrganization()
  const orgId = organization?.id
  const snippetsEnabled = useSnippetsEnabled()

  const snippetsLatch = useRef({ orgId, on: false })
  if (snippetsLatch.current.orgId !== orgId) {
    snippetsLatch.current = { orgId, on: false }
  }
  snippetsLatch.current.on ||= snippetsEnabled

  const snippetsOn = Boolean(snippets) && snippetsLatch.current.on
  const snippetsInteractive = snippetsOn && inferredMode === 'edit'
  // Only while snippets are on: passing a duty id through a disabled source
  // would key a query that never runs, and reopen it on every org switch.
  const snippetDutyId = snippetsOn ? snippets?.dutyId : undefined
  const snippetSource = useSnippetReferenceSource(snippetsOn, snippetDutyId)
  // A zustand action, so its identity is stable — the NodeView reads it
  // once from the Milkdown ctx and would otherwise capture a stale one.
  const openSnippetEditor = useSnippetEditorStore(
    (state) => state.openSnippetEditor
  )

  // The editor ctx the toolbar button or `/snippet` captured; null means closed.
  const [snippetTarget, setSnippetTarget] = useState<Ctx | null>(null)

  return {
    snippetsOn,
    snippetsInteractive,
    snippetDutyId,
    snippetSource,
    openSnippetEditor,
    snippetTarget,
    setSnippetTarget,
  }
}
